import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Expand,
  LogOut,
  Minimize2,
  Radio,
  RefreshCw,
  Volume2,
  VolumeX,
  Wifi,
  WifiOff,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Alert } from "../../components/Alert";
import { Avatar } from "../../components/Avatar";
import { Badge, RoomStatusBadge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Spinner } from "../../components/Spinner";
import { StatsPanel } from "../../components/StatsPanel";
import { api, errorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import { formatResolution } from "../../lib/format";
import {
  RoomSocket,
  type RoomSocketStatus,
  type ServerMessage,
} from "../../lib/signaling";
import type { ParticipantConnectionStats, Room } from "../../lib/types";
import { StreamReceiver } from "../../lib/webrtc";

type ViewerStatus =
  | "connecting"
  | "waiting"
  | "connected"
  | "reconnecting"
  | "failed"
  | "full"
  | "not_live"
  | "kicked"
  | "ended"
  | "error";

const statusText: Record<ViewerStatus, string> = {
  connecting: "Устанавливаем соединение…",
  waiting: "Ожидаем видеопоток от ведущего…",
  connected: "В эфире",
  reconnecting: "Восстанавливаем соединение…",
  failed: "Соединение потеряно",
  full: "В комнате нет свободных мест",
  not_live: "Трансляция ещё не началась",
  kicked: "Ведущий отключил вас",
  ended: "Трансляция завершена",
  error: "Ошибка подключения",
};

const socketStatusText: Record<RoomSocketStatus, string> = {
  connecting: "Подключаемся к signaling…",
  open: "Signaling подключён",
  reconnecting: "Переподключение…",
  closed: "Signaling отключён",
};

export function ViewerRoom({
  room,
  onRoomChange,
  onEnded,
}: {
  room: Room;
  onRoomChange: (room: Room) => void;
  onEnded: (room: Room) => void;
}) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<ViewerStatus>("connecting");
  const [socketStatus, setSocketStatus] =
    useState<RoomSocketStatus>("connecting");
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [stats, setStats] = useState<ParticipantConnectionStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [muted, setMuted] = useState(true);
  const [hasAudio, setHasAudio] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [selfId, setSelfId] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [showStats, setShowStats] = useState(true);

  const socketRef = useRef<RoomSocket | null>(null);
  const receiverRef = useRef<StreamReceiver | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const onRoomChangeRef = useRef(onRoomChange);
  const onEndedRef = useRef(onEnded);
  const handleMessageRef = useRef<(message: ServerMessage) => void>(
    () => undefined,
  );
  const endedRef = useRef(false);

  onRoomChangeRef.current = onRoomChange;
  onEndedRef.current = onEnded;

  const finishWithRoom = (nextRoom: Room, message?: string) => {
    if (endedRef.current) return;
    endedRef.current = true;
    receiverRef.current?.stop();
    setStatus("ended");
    if (message) setNotice(message);
    setRemoteStream(null);
    setStats(null);
    onEndedRef.current(nextRoom);
  };

  const handleEnded = async (message = "Ведущий завершил трансляцию.") => {
    if (endedRef.current) return;
    try {
      const response = await api.getRoom(room.id);
      finishWithRoom(response.room, message);
    } catch {
      finishWithRoom(
        { ...room, status: "ended", endedAt: new Date().toISOString() },
        message,
      );
    }
  };

  useEffect(() => {
    let disposed = false;
    const init = async () => {
      try {
        const ice = await api.iceConfig();
        if (disposed) return;

        const receiver = new StreamReceiver(room.id, ice, {
          onSignal: (payload) => socketRef.current?.send(payload),
          onTrack: (stream) => {
            if (disposed) return;
            setRemoteStream(stream);
            setHasAudio(stream.getAudioTracks().length > 0);
            setStatus((current) =>
              current === "ended" || current === "kicked" || current === "full"
                ? current
                : "connected",
            );
            setError(null);
          },
          onState: (state) => {
            if (disposed || endedRef.current) return;
            if (state === "connected") {
              setStatus("connected");
              setError(null);
            } else if (state === "connecting" || state === "new") {
              setStatus((current) =>
                current === "connected" || current === "reconnecting"
                  ? current
                  : "connecting",
              );
            } else if (state === "disconnected") {
              setStatus("reconnecting");
            } else if (state === "failed") {
              setStatus("failed");
              setError(
                "WebRTC-соединение потеряно. Пытаемся восстановить канал.",
              );
            }
          },
          onStats: (nextStats) => {
            if (!disposed) setStats(nextStats);
          },
          onError: (message) => {
            if (!disposed) setError(message);
          },
        });
        receiverRef.current = receiver;

        const socket = new RoomSocket({
          roomId: room.id,
          onMessage: (message) => handleMessageRef.current(message),
          onStatus: (nextStatus) => {
            if (!disposed) setSocketStatus(nextStatus);
          },
          onFatal: (code, message) => {
            if (disposed || endedRef.current) return;
            if (code === "ROOM_FULL") {
              setStatus("full");
              setError(message);
            } else if (code === "ROOM_NOT_LIVE") {
              setStatus("not_live");
              setError(message);
            } else if (code === "FORBIDDEN" || code === "ROOM_NOT_FOUND") {
              setStatus("error");
              setError(message);
            } else if (code === "CONNECTION_LOST") {
              setStatus("failed");
              setError(message);
            } else {
              setError(message);
            }
          },
        });
        socketRef.current = socket;
        socket.connect();
      } catch (caught) {
        if (!disposed) {
          setStatus("error");
          setError(errorMessage(caught));
        }
      }
    };
    void init();

    return () => {
      disposed = true;
      receiverRef.current?.stop();
      receiverRef.current = null;
      socketRef.current?.destroy();
      socketRef.current = null;
    };
  }, [room.id]);

  handleMessageRef.current = (message: ServerMessage) => {
    switch (message.type) {
      case "room:joined": {
        setSelfId(message.selfId);
        const nextRoom: Room = { ...message.room, role: "viewer" };
        onRoomChangeRef.current(nextRoom);
        if (nextRoom.status === "ended") {
          void handleEnded();
          break;
        }
        setStatus((current) => {
          if (current === "connected" || current === "reconnecting")
            return current;
          return "waiting";
        });
        break;
      }
      case "room:updated": {
        const nextRoom: Room = { ...message.room, role: "viewer" };
        onRoomChangeRef.current(nextRoom);
        if (nextRoom.status === "ended") {
          void handleEnded();
        }
        break;
      }
      case "peer:joined":
        if (message.peer.role === "host") {
          setStatus((current) =>
            current === "connected" ? current : "connecting",
          );
          setError(null);
        }
        break;
      case "peer:left":
        if (message.role === "host") {
          setStatus((current) =>
            current === "ended" ? current : "reconnecting",
          );
          setError(
            "Ведущий временно отключился. Ждём восстановления соединения…",
          );
        }
        break;
      case "webrtc:offer":
        setStatus((current) =>
          current === "ended" || current === "kicked" || current === "full"
            ? current
            : "connecting",
        );
        receiverRef.current?.handleOffer(message.fromUserId, message.sdp);
        break;
      case "webrtc:ice":
        receiverRef.current?.handleIce(message.candidate);
        break;
      case "webrtc:answer":
      case "webrtc:restart-request":
        break;
      case "viewer:kicked":
        setStatus("kicked");
        setError("Ведущий отключил вас от просмотра.");
        receiverRef.current?.stop();
        socketRef.current?.destroy();
        break;
      case "room:closed":
        void handleEnded(
          message.reason === "host_stopped"
            ? "Ведущий завершил трансляцию."
            : "Трансляция завершена: ведущий отключился.",
        );
        break;
      case "error":
        if (message.code === "ROOM_FULL") setStatus("full");
        else if (message.code === "ROOM_NOT_LIVE") setStatus("not_live");
        setError(message.message);
        break;
      case "room:left":
      case "pong":
        break;
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.srcObject = remoteStream;
    video.muted = muted;
    if (remoteStream) {
      void video.play().catch(() => {
        setNotice(
          "Браузер заблокировал автозапуск. Нажмите на видео или включите звук.",
        );
      });
    }
    return () => {
      if (video.srcObject === remoteStream) video.srcObject = null;
    };
  }, [remoteStream, muted]);

  useEffect(() => {
    const onFullscreenChange = () =>
      setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await containerRef.current?.requestFullscreen();
      }
    } catch {
      setError("Браузер не разрешил полноэкранный режим.");
    }
  };

  const enableSound = async () => {
    setMuted(false);
    try {
      await videoRef.current?.play();
    } catch {
      setNotice("Нажмите на видео, чтобы включить звук.");
    }
  };

  const leave = async () => {
    if (leaving) return;
    setLeaving(true);
    try {
      await api.leaveRoom(room.id);
    } catch {
      // The socket close will remove the viewer as well.
    } finally {
      socketRef.current?.send({ type: "room:leave", roomId: room.id });
      socketRef.current?.destroy();
      receiverRef.current?.stop();
      navigate("/dashboard");
    }
  };

  const connectionTone =
    status === "connected"
      ? "emerald"
      : status === "connecting" ||
          status === "waiting" ||
          status === "reconnecting"
        ? "amber"
        : "rose";

  const currentResolution =
    stats?.width && stats?.height
      ? formatResolution(stats.width, stats.height)
      : room.settings
        ? formatResolution(room.settings.width, room.settings.height)
        : null;

  return (
    <div className="pb-10">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <Link
            to="/dashboard"
            className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-white/[0.07] bg-white/[0.02] text-zinc-500 transition-colors hover:border-white/[0.14] hover:text-zinc-200"
            aria-label="Назад в кабинет"
          >
            <ArrowLeft className="size-4" />
          </Link>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-xl font-semibold tracking-[-0.03em] text-white sm:text-2xl">
                {room.title}
              </h1>
              <RoomStatusBadge status={room.status} />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-zinc-500">
              <span className="inline-flex items-center gap-1.5">
                <Avatar
                  name={room.owner.displayName}
                  size="sm"
                  className="size-5 text-[8px]"
                />
                {room.owner.displayName}
              </span>
              <span className="text-zinc-800">·</span>
              <span className="inline-flex items-center gap-1.5">
                <Radio className="size-3" />
                {room.viewerCount}/{room.maxViewers} зрителей
              </span>
              {currentResolution && (
                <>
                  <span className="text-zinc-800">·</span>
                  <span className="font-mono text-[10px]">
                    {currentResolution}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={connectionTone as "emerald" | "amber" | "rose"}>
            <span
              className={cn(
                "size-1.5 rounded-full",
                connectionTone === "emerald" && "bg-emerald-400",
                connectionTone === "amber" && "bg-amber-400",
                connectionTone === "rose" && "bg-rose-400",
              )}
            />
            {statusText[status]}
          </Badge>
          <Button
            variant="danger"
            size="md"
            onClick={() => void leave()}
            loading={leaving}
          >
            {!leaving && <LogOut className="size-4" />}
            Выйти
          </Button>
        </div>
      </div>

      {error && (
        <Alert
          tone={status === "reconnecting" ? "warning" : "error"}
          className="mb-4"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>{error}</span>
            <button
              type="button"
              className="text-[11px] underline decoration-current/30 underline-offset-2 hover:text-white"
              onClick={() => setError(null)}
            >
              Скрыть
            </button>
          </div>
        </Alert>
      )}
      {notice && (
        <Alert tone="info" className="mb-4">
          <div className="flex items-center justify-between gap-3">
            <span>{notice}</span>
            <button
              type="button"
              className="text-[11px] text-cyan-200/60 hover:text-white"
              onClick={() => setNotice(null)}
            >
              Ок
            </button>
          </div>
        </Alert>
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_330px]">
        <div
          ref={containerRef}
          className={cn(
            "group relative isolate overflow-hidden rounded-2xl border border-white/[0.08] bg-black shadow-[0_25px_100px_-35px_rgba(0,0,0,0.95)]",
            fullscreen
              ? "flex h-screen w-screen items-center justify-center"
              : "aspect-video",
          )}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={muted}
            onClick={() => {
              if (muted && hasAudio) void enableSound();
              else void videoRef.current?.play().catch(() => undefined);
            }}
            className="absolute inset-0 size-full object-contain"
            aria-label="Трансляция экрана"
          />

          {!remoteStream && status !== "ended" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-[radial-gradient(circle_at_50%_40%,rgba(139,92,246,0.08),transparent_60%)] px-6 text-center">
              {status === "full" ||
              status === "not_live" ||
              status === "kicked" ? (
                <AlertTriangle className="mb-4 size-7 text-amber-300/80" />
              ) : (
                <Spinner className="mb-4 size-7" />
              )}
              <p className="text-[15px] font-medium text-zinc-200">
                {statusText[status]}
              </p>
              <p className="mt-1.5 max-w-sm text-[12px] leading-relaxed text-zinc-500">
                {status === "waiting" || status === "connecting"
                  ? "Как только ведущий начнёт передачу, видео появится автоматически. Ничего нажимать не нужно."
                  : status === "reconnecting"
                    ? "Соединение с ведущим временно прервано. Ждём повторного offer."
                    : status === "full"
                      ? "Освободится место — можно будет попробовать снова."
                      : status === "not_live"
                        ? "Вернитесь в кабинет: комната появится в активных после старта."
                        : status === "kicked"
                          ? "Вы можете вернуться в кабинет и подключиться к другой комнате."
                          : "Проверьте соединение и попробуйте снова."}
              </p>
              {(status === "full" ||
                status === "not_live" ||
                status === "kicked" ||
                status === "failed") && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-4"
                  onClick={() => void leave()}
                >
                  <ArrowLeft className="size-3.5" />В кабинет
                </Button>
              )}
            </div>
          )}

          <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between gap-3 bg-gradient-to-b from-black/60 to-transparent p-3 opacity-0 transition-opacity group-hover:opacity-100">
            <div className="flex items-center gap-2">
              <Badge tone="rose">LIVE</Badge>
              <span className="rounded-md border border-white/10 bg-black/45 px-2 py-1 text-[10px] text-white/75 backdrop-blur">
                {room.title}
              </span>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-black/45 px-2 py-1 text-[10px] text-white/70 backdrop-blur">
              {status === "connected" ? (
                <Wifi className="size-3" />
              ) : (
                <WifiOff className="size-3" />
              )}
              {socketStatusText[socketStatus]}
            </span>
          </div>

          <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-black/80 via-black/35 to-transparent p-3 opacity-0 transition-opacity group-hover:opacity-100">
            <div className="flex items-center gap-1.5">
              {hasAudio && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="border-white/10 bg-black/45 text-white backdrop-blur hover:bg-black/65"
                  onClick={() => (muted ? void enableSound() : setMuted(true))}
                >
                  {muted ? (
                    <VolumeX className="size-3.5" />
                  ) : (
                    <Volume2 className="size-3.5" />
                  )}
                  {muted ? "Включить звук" : "Звук"}
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                className="border-white/10 bg-black/45 text-white backdrop-blur hover:bg-black/65"
                onClick={() => setShowStats((value) => !value)}
              >
                <Wifi className="size-3.5" />
                {showStats ? "Скрыть статистику" : "Статистика"}
              </Button>
            </div>
            <div className="flex items-center gap-1.5">
              <Button
                variant="secondary"
                size="sm"
                className="border-white/10 bg-black/45 text-white backdrop-blur hover:bg-black/65"
                onClick={() => void toggleFullscreen()}
              >
                {fullscreen ? (
                  <Minimize2 className="size-3.5" />
                ) : (
                  <Expand className="size-3.5" />
                )}
                {fullscreen ? "Выйти" : "На весь экран"}
              </Button>
            </div>
          </div>

          {fullscreen && (
            <div className="pointer-events-none absolute left-4 top-4 flex items-center gap-2">
              <Badge tone="rose">LIVE</Badge>
              <span className="rounded-md border border-white/10 bg-black/50 px-2 py-1 text-[11px] text-white/80">
                {room.title}
              </span>
            </div>
          )}
        </div>

        <div className={cn("space-y-4", !showStats && "hidden xl:block")}>
          <div className="rounded-2xl border border-white/[0.07] bg-[#0c0e14]/80 p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <Wifi className="size-4 text-zinc-600" />
                <h2 className="text-[14px] font-medium text-zinc-200">
                  Соединение
                </h2>
              </div>
              <Badge tone={connectionTone as "emerald" | "amber" | "rose"}>
                {status === "connected" ? "Подключено" : statusText[status]}
              </Badge>
            </div>
            <StatsPanel stats={stats} />
            <div className="mt-4 flex items-center justify-between border-t border-white/[0.05] pt-3 text-[10px] text-zinc-600">
              <span>Обновление ~1 раз в секунду</span>
              <span className="font-mono">
                {selfId ? selfId.slice(0, 8) : "peer"} ·{" "}
                {socketStatusText[socketStatus]}
              </span>
            </div>
          </div>

          <div className="rounded-2xl border border-white/[0.07] bg-[#0c0e14]/80 p-5">
            <h2 className="text-[14px] font-medium text-zinc-200">
              О трансляции
            </h2>
            <p className="mt-2 text-[12px] leading-relaxed text-zinc-500">
              {room.description || "Описание не указано."}
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3 border-t border-white/[0.05] pt-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-zinc-700">
                  Ведущий
                </p>
                <p className="mt-1 truncate text-[12px] text-zinc-300">
                  {room.owner.displayName}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-zinc-700">
                  Зрители
                </p>
                <p className="mt-1 font-mono text-[12px] text-zinc-300">
                  {room.viewerCount} / {room.maxViewers}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2 rounded-xl border border-white/[0.05] bg-white/[0.012] px-3.5 py-3 text-[10px] leading-relaxed text-zinc-600">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            Запись не ведётся. Поток существует только пока ведущий показывает
            экран.
          </div>
        </div>
      </div>
    </div>
  );
}
